import { useQuery } from '@tanstack/react-query';
import { authFetch } from '../../shared/api/authFetch';
import { admin_apiConfig } from '../../shared/config/config';
import { storeInvitationToken } from '../auth/pages/AcceptTeamInvitationPage';

export const ACCOUNT_CLAIM_QUERY_KEY = ['accountClaimStatus'];
export const CLAIM_PAGE_PATH = '/accept-invitation';

/** Whether the signed-in profile is a preconfigured account nobody has claimed (ADR-0044). */
async function loadClaimStatus() {
  const response = await authFetch(`${admin_apiConfig.baseURL}/api/organization/me`);
  if (!response.ok) throw new Error(`Account status unavailable (${response.status}).`);
  const body = await response.json();
  return {
    preconfigured: body?.preconfigured === true,
    username: body?.preconfigured_username || null,
  };
}

export const useAccountClaimStatus = ({ enabled = true } = {}) =>
  useQuery({
    enabled,
    queryKey: ACCOUNT_CLAIM_QUERY_KEY,
    queryFn: loadClaimStatus,
    staleTime: Infinity,
    retry: false,
  });

/** The account was already claimed or has an emailed claim invitation waiting. */
export class ClaimUnavailableError extends Error {}

/**
 * Issues the account a short-lived self-claim ticket and keeps it where the claim
 * page reads it, so it survives the Google sign-in redirect.
 */
export async function startSelfClaim() {
  const response = await authFetch(`${admin_apiConfig.baseURL}/api/organization/team/self-claim`, { method: 'POST' });
  const body = await response.json().catch(() => ({}));
  if (response.status === 409) {
    // The server says which: already claimed, or an emailed invitation to use instead.
    throw new ClaimUnavailableError(typeof body?.detail === 'string' && body.detail
      ? body.detail
      : 'An invitation to claim this account has already been emailed. Use the link in that email.');
  }
  if (!response.ok || !body?.token) {
    throw new Error(typeof body?.detail === 'string' ? body.detail : 'Making this account yours could not start. Try again.');
  }
  storeInvitationToken(body.token);
  return body;
}
